import { safeParseFloat } from '@/lib/utils'
import { Denomination, OrderSide, type Position } from '@/types/generated/common/trading'

const QUOTE_POSITION_ZERO_EPSILON = 1e-8

/**
 * Merges position updates into existing positions and removes closed positions.
 * Kept independent from the React/WebSocket manager so it can be tested without
 * loading browser services or environment configuration.
 */
export function mergePositions(prev: Position[], updates: Position[]): Position[] {
  const result = [...prev]

  updates.forEach((newPos) => {
    const index = result.findIndex(
      (position) => position.symbol === newPos.symbol && position.accountId === newPos.accountId
    )

    if (isClosedPositionUpdate(newPos)) {
      if (index !== -1) result.splice(index, 1)
      return
    }

    if (index !== -1) {
      const oldPos = result[index]
      result[index] = {
        ...oldPos,
        ...Object.fromEntries(
          Object.entries(newPos).filter(([key, value]) => {
            if (value === '' || value === undefined) return false
            if (key === 'side' && value === OrderSide.ORDER_SIDE_UNSPECIFIED) return false
            return true
          })
        ),
      }
    } else if (newPos.quantity !== '') {
      result.push(newPos)
    }
  })

  return result
}

export type PositionsFrame = {
  accountId: string
  positions: Position[]
  /** The server's `PositionList.isFullSnapshot`: the account's complete open set. */
  isFullSnapshot: boolean
}

/**
 * Applies one flush window's frames in arrival order: a full snapshot replaces
 * its account's rows and keeps every other account's, a delta merges.
 */
export function applyPositionFrames(
  prev: Position[],
  frames: readonly PositionsFrame[]
): Position[] {
  return frames.reduce((rows, frame) => {
    if (!frame.isFullSnapshot) return mergePositions(rows, frame.positions)
    const otherAccounts = rows.filter((p) => p.accountId && p.accountId !== frame.accountId)
    return mergePositions(otherAccounts, frame.positions)
  }, prev)
}

function isClosedPositionUpdate(position: Position) {
  if (position.quantity === '') return false

  const quantity = safeParseFloat(position.quantity)
  if (!Number.isFinite(quantity)) return false
  if (quantity === 0) return true

  return (
    position.sizeDenomination === Denomination.DENOMINATION_QUOTE &&
    Math.abs(quantity) <= QUOTE_POSITION_ZERO_EPSILON
  )
}
